<template>
    <div class="popupPostback">
        <ValidationObserver ref="form" v-slot="{handleSubmit, invalid}">
            <vs-row>
                <vs-col vs-w="6">
                    <div class="pl-2 pr-2 mb-4">
                        <ValidationProvider vid="name" :name="$t('global.Name')" rules="required|minmax:3,50" v-slot="{ errors }">
                            <div class="vs-input--label">{{ $t('global.Name') }}</div>
                            <vs-input
                                class="w-full"
                                :placeholder="$t('global.Name')"
                                v-model="name"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </ValidationProvider>
                    </div>
                </vs-col>
                <vs-col vs-w="6">
                    <div class="pl-2 pr-2 mb-4">
                        <ValidationProvider vid="event_type" :name="$t('label.Event_type')" rules="required"
                                            v-slot="{ errors }">
                            <div class="vs-input--label">{{ $t('label.Event_type') }}</div>
                            <v-select
                                class="w-full"
                                :placeholder="$t('label.Event_type')"
                                v-model="event_type"
                                :options="optionsEventType"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </ValidationProvider>
                    </div>
                </vs-col>
                <vs-col vs-w="6" v-if="user.group === 2">
                    <div class="pl-2 pr-2 mb-4">
                        <ValidationProvider vid="team" :name="$t('global.Team')" rules="required" v-slot="{ errors }">
                            <div class="vs-input--label">{{ $t('global.Team') }}</div>
                            <v-select
                                class="w-full"
                                :placeholder="$t('global.Team')"
                                v-model="team"
                                :options="optionsTeams"
                                label="name"
                                :reduce="item => item.id"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </ValidationProvider>
                    </div>
                </vs-col>
                <vs-col vs-w="12">
                    <div class="pl-2 pr-2 mb-4">
                        <ValidationProvider vid="url" :name="$t('campaigns.Url')" rules="required|url" v-slot="{ errors }">
                            <div class="vs-input--label">{{ $t('campaigns.Url') }}</div>
                            <vs-input
                                class="w-full"
                                :placeholder="$t('campaigns.Url')"
                                v-model="url"
                                @input="uri"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </ValidationProvider>
                    </div>
                </vs-col>
                <vs-col vs-w="12">
                    <div class="pl-2 pr-2 mb-4">
                        <ValidationProvider vid="preview_url" :name="$t('modals.Preview_url')" rules="required"
                                            v-slot="{ errors }">
                            <div class="vs-input--label flex items-end justify-between">
                                <span>{{ $t('modals.Preview_url') }}</span>

                                <vs-icon :class="{'disabled': !preview_url}"
                                         class="m-4 cursor-pointer"
                                         color="primary"
                                         v-tooltip="$t('global.Copy_url')"
                                         @click.stop.prevent="copy(preview_url)"
                                         v-bind="$iconData('icon-copy', 'fi-rr-copy-alt')"

                                />
                            </div>
                            <vs-textarea
                                disabled
                                class="w-full m-0"
                                :placeholder="$t('modals.Preview_url')"
                                :value="`${preview_url}`"
                            />
                            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                        </ValidationProvider>
                    </div>
                </vs-col>
            </vs-row>

            <vs-row v-for="(parameter, index) of parameters" :key="index" vs-type="flex" vs-align="center">
                <vs-col vs-w="5">
                    <div class="pl-2 pr-2 mb-4">
                        <div class="vs-input--label">{{ $t('label.Parameter_name') }}</div>
                        <vs-input
                            @input="params()"
                            class="w-full"
                            name="parameter_name"
                            :placeholder="$t('label.Parameter_name')"
                            v-model.trim="parameter.send"
                            :disabled="invalid"
                        />
                    </div>
                </vs-col>
                <vs-col vs-w="5">
                    <div class="pl-2 pr-2 mb-4 flex items-end">
                        <div class="mb-2">
                            <vs-checkbox
                                v-tooltip="$t('global.Custom')"
                                @input="params(index)"
                                v-model="parameter.custom"
                                :disabled="invalid"
                            ></vs-checkbox>
                        </div>
                        <div class="w-full">
                            <div class="vs-input--label">{{ $t('global.Value') }}</div>
                            <vs-input
                                @input="params()"
                                v-if="parameter.custom"
                                v-model.trim="parameter.get"
                                class="w-full"
                                name="value"
                                :placeholder="$t('global.Value')"
                                :disabled="invalid"
                            />
                            <v-select
                                @input="params()"
                                v-else
                                v-model="parameter.get"
                                class="w-full"
                                name="value"
                                :placeholder="$t('global.Value')"
                                :options="optionsSub"
                                :disabled="invalid"
                            />
                        </div>
                    </div>
                </vs-col>
                <vs-col vs-w="2">
                    <div class="flex items-center">
                        <vs-button
                            v-if="parameters.length > 1"
                            :class="{'mr-2': parameters.length > 1}"
                            @click="removeParameter(index)"
                            v-tooltip="$t('buttons.Delete')"
                            radius
                            color="primary"
                            type="filled"
                            v-bind="$iconData('icon-minus', 'fi-rr-minus-small')"
                            :disabled="invalid"
                        ></vs-button>

                        <vs-button
                            v-if="(parameters.length - 1) === index"
                            @click="addParameter"
                            v-tooltip="$t('buttons.Add_parameter')"
                            radius
                            color="primary"
                            type="filled"
                            v-bind="$iconData('icon-plus', 'fi-rr-plus')"
                            :disabled="invalid"
                        ></vs-button>
                    </div>
                </vs-col>
            </vs-row>

            <div class="pl-2 pr-2 flex justify-end">
                <vs-button @click="handleSubmit(send)" color="primary" type="filled">{{ $t('buttons.Save') }}
                </vs-button>
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import vSelect from 'vue-select'
import ErrorHandler from '@/utils/errorHandler'
import send from '@/utils/fetch'
import EventBus from '@/store/eventBus'
import {copy} from '@/utils/utils'
import {utils} from "@/utils/mixins"
import {ValidationObserver, ValidationProvider} from 'vee-validate'

export default {
    props: ['popupData', 'popupType', 'popupButton'],

    mixins: [utils],

    data() {
        return {
            name: '',
            event_type: '',
            url: '',
            preview_url: '',
            parameters: [{send: '', custom: false, get: ''}],
            affiliate: '',
            team: '',
            send_parameter: '',

            optionsAffiliates: [],
            optionsTeams: [],
            optionsEventType: ['lead', 'status', 'ftd', 'trash'],
            sendParams: ['group_status', 'lead_id'],
            optionsSub: ['sub1', 'sub2', 'sub3', 'sub4', 'sub5', 'sub6', 'sub7', 'sub8', 'external_price', 'group_status', 'id'],

            teamsArray: []
        }
    },

    components: {
        ValidationProvider,
        ValidationObserver,
        vSelect
    },

    computed: {
        user() {
            return this.$store.getters.userState
        }
    },

    methods: {
        addParameter() {
            this.parameters.push({
                get: '',
                send: null,
                custom: false
            })
        },

        removeParameter(index) {
            this.parameters.splice(index, 1)
            this.params()
        },

        uri(value) {
            this.preview_url = this.parameters.length ? this.params() : value
        },

        copy(copyData) {
            const result = copy(copyData)
            const notify = {}

            if(result) {
                notify.title = 'Success',
                    notify.icon = 'check-circle'
                notify.text = 'Copy'
                notify.color = 'success'
            } else {
                notify.title = 'Error',
                    notify.icon = 'alert-circle'
                notify.color = 'error'
            }

            this.$vs.notify({
                title: notify.title,
                text: notify.text,
                color: notify.color,
                iconPack: 'feather',
                icon: `icon-${notify.icon}`
            })
        },

        params(index) {
            try {
                if (
                    index !== '' &&
                    index !== undefined &&
                    index !== null &&
                    !isNaN(index)
                ) {
                    this.parameters[index].get = ''
                }

                const list = this.parameters
                    .filter(({ get, send }) => get && send)
                    .map(({ send, get, custom }) => `${send}=${custom ? get : `{${get}}`}`)

                this.preview_url = list.length ? `${this.url}?${list.join('&')}` : this.url

                return this.preview_url
            } catch (e) {
                new ErrorHandler(e).send()
            }
        },

        copyData(data) {
            return JSON.parse(JSON.stringify(data))
        },

        async send() {
            try {
                let params = this.copyData(this.parameters)

                if (Array.isArray(params)) {
                    params = params.filter(item => item.get && item.send)
                }

                const data = {
                    name: this.name.trim(),
                    params: params,
                    url: this.url.trim(),
                    type: this.event_type,
                    is_active: 1,
                    team_id: this.team ? this.team : this.user.team_id
                }

                if(this.popupType === 'addPostback') {
                    await send(this.$url.postback.create, 'POST', {data})
                }

                if(this.popupType === 'editPostback') {
                    data.id = this.popupData.id
                    await send(this.$url.postback.update, 'PUT', {data})
                }

                EventBus.$emit('success_postback', {type: 'close'})
                EventBus.$emit('emitPopupEvent', {type: 'close'})
            } catch(error) {
                if(error.response) {
                    const response = error.response.data.result
                    this.$refs.form.setErrors(this.$errors(response))
                }
                new ErrorHandler(error).send
            }
        },

        _inData(prop, name) {
            return prop[name] ? prop[name] : ''
        },

        async getPostbackData() {
            try {
                const response = await send(this.$url.postback.getPostbackData)
                    .then(response => response.data.result.teams)

                this.teamsArray = this.createNameById(response)

                this.optionsTeams = response
            } catch (e) {
                new ErrorHandler(e).send
            }
        }
    },

    async mounted() {
        try {
            await this.getPostbackData()

            if(this.popupType === 'editPostback') {
                const popupData = this.copyData(this.popupData)

                this.name = this._inData(popupData, 'name')
                this.event_type = this._inData(popupData, 'type')

                this.team = popupData.team_id

                this.url = this._inData(popupData, 'url')

                if (Array.isArray(popupData.params) && popupData.params.length) {
                    this.parameters = popupData.params
                } else {
                    this.parameters = [{
                        get: '',
                        send: null,
                        custom: false
                    }]
                }

                this.params()
            }

            this.$store.commit('UPDATE_LOADER_STATE', false)
        } catch (e) {
            this.$store.commit('UPDATE_LOADER_STATE_SET_ERROR', true)
            new ErrorHandler(e).send
        }
    }
}
</script>

<style lang="scss">
.popupPostback {
    .disabled {
        opacity: .5;
        cursor: default;
        pointer-events: none;
    }

    input {
        &::placeholder {
            color: rgba(0, 0, 0, 0.2);
        }
    }

    .vs-inputx {
        box-shadow: none;
        border: 1px solid rgba(0, 0, 0, 0.2) !important;
    }

    .vs-textarea {
        resize: none !important;
        height: 110px;

        &::placeholder {
            color: rgba(0, 0, 0, 0.2);
        }
    }
}
</style>
